/**
 * Client-side validation with the same JSON Schema files the Lambda uses.
 * Files are imported straight from backend/seldon_tasks/schemas via the @schemas alias.
 * `$ref: "<name>.schema.json"` is inlined exactly like the Python loader does.
 */
import Ajv, { type ErrorObject } from 'ajv'
import addFormats from 'ajv-formats'
import draftBrief from '@schemas/draft_brief.schema.json'
import draftCriterion from '@schemas/draft_criterion.schema.json'
import draftContentUpdate from '@schemas/draft_content_update.schema.json'
import taskBrief from '@schemas/task_brief.schema.json'
import taskCreate from '@schemas/task_create.schema.json'
import taskUpdate from '@schemas/task_update.schema.json'
import contentUpdate from '@schemas/content_update.schema.json'
import submissionUpdate from '@schemas/submission_update.schema.json'
import rubricCriterion from '@schemas/rubric_criterion.schema.json'
import reviewUpdate from '@schemas/review_update.schema.json'
import rubricLayout from '@schemas/rubric_layout.schema.json'
import rubricUpdate from '@schemas/rubric_update.schema.json'
import program from '@schemas/program.schema.json'
import coachMessage from '@schemas/coach_message.schema.json'
import coachFileRegister from '@schemas/coach_file_register.schema.json'
import templateResearchStart from '@schemas/template_research_start.schema.json'

import reviewRecommendation from '@schemas/review_recommendation.schema.json'
import reviewComment from '@schemas/review_comment.schema.json'
import draftCoachReview from '@schemas/draft_coach_review.schema.json'
import coachReviewWaiver from '@schemas/coach_review_waiver.schema.json'

const raw: Record<string, unknown> = {
  review_recommendation: reviewRecommendation,
  review_comment: reviewComment,
  draft_coach_review: draftCoachReview,
  coach_review_waiver: coachReviewWaiver,
  rubric_update: rubricUpdate,
  rubric_layout: rubricLayout,
  task_brief: taskBrief,
  draft_brief: draftBrief,
  draft_criterion: draftCriterion,
  draft_content_update: draftContentUpdate,
  task_create: taskCreate,
  task_update: taskUpdate,
  content_update: contentUpdate,
  submission_update: submissionUpdate,
  rubric_criterion: rubricCriterion,
  review_update: reviewUpdate,
  program,
  coach_message: coachMessage,
  coach_file_register: coachFileRegister,
  template_research_start: templateResearchStart,
}

const SUFFIX = '.schema.json'

function inline(node: unknown, depth = 0): unknown {
  if (depth > 20) throw new Error('Schema $ref nesting too deep')
  if (Array.isArray(node)) return node.map((n) => inline(n, depth + 1))
  if (node && typeof node === 'object') {
    const obj = node as Record<string, unknown>
    const ref = obj.$ref
    if (typeof ref === 'string' && ref.endsWith(SUFFIX)) {
      const name = ref.slice(0, -SUFFIX.length)
      const target = { ...(raw[name] as Record<string, unknown>) }
      delete target.$schema
      delete target.$id
      const { $ref: _drop, ...rest } = obj
      return inline({ ...target, ...rest }, depth + 1)
    }
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, inline(v, depth + 1)]))
  }
  return node
}

const ajv = new Ajv({ allErrors: true, strict: false })
addFormats(ajv)
const compiled = new Map<string, ReturnType<typeof ajv.compile>>()

function validator(name: string) {
  let v = compiled.get(name)
  if (!v) {
    const schema = inline(raw[name]) as Record<string, unknown>
    delete schema.$schema
    delete schema.$id
    v = ajv.compile(schema)
    compiled.set(name, v)
  }
  return v
}

export interface FieldError {
  path: string // e.g. "brief.common_failures" or "prompt"
  message: string
}

/** Validate `data` against `<name>.schema.json`; returns [] when valid. */
export function validate(name: string, data: unknown): FieldError[] {
  const v = validator(name)
  if (v(data)) return []
  return (v.errors ?? []).map((e: ErrorObject) => {
    let path = e.instancePath.replace(/^\//, '').replace(/\//g, '.')
    if (e.keyword === 'required') path = path ? `${path}.${(e.params as { missingProperty: string }).missingProperty}` : (e.params as { missingProperty: string }).missingProperty
    return { path, message: humanise(e) }
  })
}

function humanise(e: ErrorObject): string {
  switch (e.keyword) {
    case 'required': return 'This field is required.'
    case 'minLength': return `Please write at least ${(e.params as { limit: number }).limit} characters.`
    case 'maxLength': return `Please keep this under ${(e.params as { limit: number }).limit} characters.`
    case 'minItems': return `Please provide at least ${(e.params as { limit: number }).limit} entries.`
    case 'pattern': return 'Only letters, digits, - and _ are allowed.'
    case 'enum': return 'Please choose one of the listed options.'
    default: return e.message ?? 'Invalid value.'
  }
}

export const errorsFor = (errors: FieldError[], path: string) => errors.filter((e) => e.path === path).map((e) => e.message)
